<template>
  <div class="eligibility-simple" v-if="value">
    <!-- Eligible banners -->
    <v-alert
      v-if="value.eligible === true && value.updatedAt"
      color="success"
      :value="true"
      :closable="false"
    >
      Last check was run
      {{ new Date(value.updatedAt) | inWords({ addSuffix: true }) }}
      at {{ new Date(value.updatedAt) | date('h:mm a, MMMM do yyyy') }}
      when the candidate met all the requirements.
    </v-alert>

    <v-alert
      v-else-if="value.eligible === false && value.updatedAt"
      color="warning"
      class="black--text"
      :value="true"
      :closable="false"
    >
      Last check was run
      {{ new Date(value.updatedAt) | inWords({ addSuffix: true }) }}
      at {{ new Date(value.updatedAt) | date('h:mm a, MMMM do yyyy') }}
      when the candidate did not meet all the requirements.
    </v-alert>

    <v-alert
      v-else-if="value.eligible === null && value.updatedAt"
      color="secondary"
      class="black--text"
      :value="true"
      :closable="false"
    >
      Last check was run
      {{ new Date(value.updatedAt) | inWords({ addSuffix: true }) }}
      at {{ new Date(value.updatedAt) | date('h:mm a, MMMM do yyyy') }}
      when the candidate was potentially eligible but some requirements could not be evaluated.
    </v-alert>

    <!-- Guide layout cloned from frontend's OpportunityRequirementsGuide -->
    <div v-if="hasAnyGroups">
      <GuideRequirementGroup
        v-for="group in profileRequirements"
        :key="`profile-requirement-${group.id}`"
        :group="group"
        :first="group === profileRequirements[0]"
        :last="group === profileRequirements[profileRequirements.length - 1]"
      />
      <GuideRequirementGroup
        v-for="group in qualificationRequirements"
        :key="`qualification-requirement-${group.id}`"
        :group="group"
        :first="group === qualificationRequirements[0]"
        :last="group === qualificationRequirements[qualificationRequirements.length - 1]"
      />
      <GuideRequirementGroup
        v-for="group in experienceRequirements"
        :key="`experience-requirement-${group.id}`"
        :group="group"
        :first="group === experienceRequirements[0]"
        :last="group === experienceRequirements[experienceRequirements.length - 1]"
      />
    </div>
    <div v-else>
      No specific requirements have been set and this opportunity is open to anyone to apply.
    </div>
  </div>
</template>

<script>
// A compact requirement row
const RequirementRow = {
  functional: true,
  props: { requirement: Object, groupSatisfied: Boolean },
  render(h, { props }) {
    const r = props.requirement || {}
    const satisfied = !!r.satisfied
    const icon = satisfied ? 'mdi-check' : 'mdi-alert-outline'
    const colorClass = satisfied
      ? 'success--text'
      : r.mandatory === false
      ? 'warning--text'
      : 'error--text'
    // Fallback text if reason missing
    const text = r.reason || `${r.descriptor || ''} ${r.value || ''}`.trim()
    return h(
      'v-layout',
      { attrs: { row: true, wrap: true, 'align-center': true }, class: 'py-1 requirement-row' },
      [
        h('v-flex', { attrs: { xs1: true, sm1: true }, class: 'text-xs-center' }, [
          h('v-icon', icon),
        ]),
        h('v-flex', { attrs: { xs11: true, sm11: true } }, [
          h('span', { class: colorClass, domProps: { innerHTML: text } }),
        ]),
      ]
    )
  },
}

// Group renderer that mirrors frontend's RequirementGroup layout
const GuideRequirementGroup = {
  name: 'GuideRequirementGroup',
  props: {
    group: { type: Object, required: true },
    first: { type: Boolean, default: false },
    last: { type: Boolean, default: false },
  },
  components: { RequirementRow },
  computed: {
    header() {
      const g = this.group || {}
      if (g.type === 'ProfileRequirementGroup')
        return { text: 'Profile requirements', icon: 'mdi-pencil' }
      if (g.type === 'ProfileQualificationRequirementGroup')
        return { text: 'Qualification or study programme requirements', icon: 'mdi-school' }
      if (g.type === 'ExperienceRequirementGroup')
        return { text: 'Experience requirements', icon: 'mdi-briefcase' }
      return { text: 'Requirements', icon: 'mdi-information-outline' }
    },
    profileQualification() {
      const g = this.group || {}
      if (g.profileQualification) return g.profileQualification
      if (g.profileQualifications && g.profileQualifications.length > 0) {
        return g.profileQualifications.slice().sort((a, b) => {
          const as = (a.requirements || []).filter(r => r && r.satisfied).length
          const bs = (b.requirements || []).filter(r => r && r.satisfied).length
          return bs - as
        })[0]
      }
      return null
    },
    experience() {
      const g = this.group || {}
      if (g.experiences && g.experiences.length > 0) {
        if (g.experiences.length === 1) return g.experiences[0]
        return g.experiences.slice().sort((a, b) => {
          const as = (a.requirements || []).filter(r => r && r.satisfied).length
          const bs = (b.requirements || []).filter(r => r && r.satisfied).length
          return bs - as
        })[0]
      }
      return null
    },
    requirements() {
      if (this.profileQualification) return this.profileQualification.requirements || []
      if (this.experience) return this.experience.requirements || []
      return this.group && this.group.requirements ? this.group.requirements : []
    },
  },
  render(h) {
    const content = []
    if (this.first) {
      content.push(
        h('div', { staticClass: 'pt-4 pl-3' }, [
          h('v-chip', { attrs: { color: 'grey darken-2', dark: true } }, [
            h('v-avatar', [
              h('v-btn', { attrs: { color: 'grey darken-1', fab: true } }, [
                h('v-icon', this.header.icon),
              ]),
            ]),
            h('span', { staticClass: 'subheading nowrap pl-2' }, this.header.text),
          ]),
        ])
      )
    }

    content.push(
      h('v-container', { staticClass: 'py-2 pl-4' }, [
        h('v-layout', { attrs: { row: true, wrap: true, 'align-start': true } }, [
          this.group && this.group.name
            ? [
                h('v-flex', { attrs: { xs12: true }, class: 'py-1 pr-2' }, [
                  h(RequirementRow, {
                    props: {
                      requirement: {
                        satisfied: this.group.satisfied,
                        reason: this.group.name,
                        mandatory: this.group.mandatory,
                      },
                      groupSatisfied: this.group ? this.group.satisfied : false,
                    },
                  }),
                ]),
              ]
            : (this.requirements || []).map((req, i) =>
                h(
                  'v-flex',
                  { attrs: { xs12: true }, class: 'py-1 pr-2', key: `req-${req.id || i}` },
                  [
                    h(RequirementRow, {
                      props: {
                        requirement: req,
                        groupSatisfied: this.group ? this.group.satisfied : false,
                      },
                    }),
                  ]
                )
              ),
        ]),
      ])
    )

    if (!this.last) {
      content.push(
        h(
          'div',
          { staticClass: 'text-xs-left py-2 ml-5 grey--text text--lighten-1' },
          this.group && this.group.mandatory ? 'AND' : 'OR'
        )
      )
    }

    return h('div', { staticClass: 'requirement-group' }, content)
  },
}

export default {
  name: 'EligibilitySimple',
  props: { value: Object },
  components: { GuideRequirementGroup },
  computed: {
    groups() {
      return Array.isArray(this.value && this.value.requirementGroups)
        ? this.value.requirementGroups
        : []
    },
    profileRequirements() {
      return this.groups.filter(g => g.type === 'ProfileRequirementGroup')
    },
    qualificationRequirements() {
      return this.groups.filter(g => g.type === 'ProfileQualificationRequirementGroup')
    },
    experienceRequirements() {
      return this.groups.filter(g => g.type === 'ExperienceRequirementGroup')
    },
    hasAnyGroups() {
      return this.groups && this.groups.length > 0
    },
  },
}
</script>

<style>
.requirement-group .v-chip {
  max-width: 100%;
}
.requirement-group .v-chip .v-chip__content {
  max-width: 100%;
}
.requirement-group .nowrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.requirement-group .descriptor {
  align-self: center;
}

.eligibility-simple .requirement-row .success--text {
  color: #12c150 !important;
}
.eligibility-simple .requirement-row .error--text {
  color: #c43237 !important;
}
.eligibility-simple .requirement-row .warning--text {
  color: #f0ad4e !important;
}
</style>
